import React, { useState } from 'react'
import { useContract } from '../hooks/useContract'
import { ethers } from 'ethers'

interface TestResult {
  name: string
  status: 'pending' | 'running' | 'passed' | 'failed'
  message?: string
  details?: string
}

interface TestSuiteProps {
  onBackToMenu?: () => void
}

// AGC: Comprehensive test suite component for browser testing
export const TestSuite: React.FC<TestSuiteProps> = ({ onBackToMenu }) => {
  const [tests, setTests] = useState<TestResult[]>([
    { name: 'Wallet Connection', status: 'pending' },
    { name: 'Contract Connectivity', status: 'pending' },
    { name: 'OGB Token Airdrop', status: 'pending' },
    { name: 'Battle Cost Payment', status: 'pending' },
    { name: 'Battle Economics', status: 'pending' },
    { name: 'Account Switching', status: 'pending' },
    { name: 'Error Handling', status: 'pending' }
  ])
  
  const [isRunning, setIsRunning] = useState(false)
  const [currentTest, setCurrentTest] = useState<string>('')
  
  const {
    isConnected,
    connectWallet,
    disconnectWallet,
    addTestAccount,
    getPlayerOGBStatus,
    claimAirdrop,
    canPlayBattle,
    chargeBattleCost,
    processBattleResult,
    testMetaMaskConnection,
    signer
  } = useContract()

  // AGC: Update test status
  const updateTest = (name: string, status: TestResult['status'], message?: string, details?: string) => {
    setTests(prev => prev.map(test => 
      test.name === name 
        ? { ...test, status, message, details }
        : test
    ))
  }

  // AGC: Run all tests sequentially
  const runAllTests = async () => {
    if (isRunning) return
    
    setIsRunning(true)
    setCurrentTest('')
    
    // AGC: Reset all tests
    setTests(prev => prev.map(test => ({ ...test, status: 'pending', message: undefined, details: undefined })))
    
    try {
      await testWalletConnection()
      await testContractConnectivity()
      await testOGBAirdrop()
      await testBattleCostPayment()
      await testAccountSwitching()
      await testErrorHandling()
      
      setCurrentTest('✅ All tests completed!')
    } catch (error) {
      console.error('Test suite failed:', error)
      setCurrentTest(`❌ Test suite failed: ${error.message}`)
    } finally {
      setIsRunning(false)
    }
  }

  // AGC: Test 1 - Wallet Connection
  const testWalletConnection = async () => {
    setCurrentTest('Testing wallet connection...')
    updateTest('Wallet Connection', 'running')
    
    try {
      if (!isConnected) {
        await connectWallet()
      }
      
      if (isConnected && signer) {
        const address = await signer.getAddress()
        updateTest('Wallet Connection', 'passed', `Connected to ${address.substring(0, 10)}...`)
      } else {
        throw new Error('Wallet not connected')
      }
    } catch (error) {
      updateTest('Wallet Connection', 'failed', error.message)
      throw error
    }
  }

  // AGC: Test 2 - Contract Connectivity
  const testContractConnectivity = async () => {
    setCurrentTest('Testing contract connectivity...')
    updateTest('Contract Connectivity', 'running')
    
    try {
      if (!signer) throw new Error('No signer available')
      
      const address = await signer.getAddress()
      const status = await getPlayerOGBStatus(address)
      
      updateTest('Contract Connectivity', 'passed', 
        `Balance: ${status.balance} OGB, Can Claim: ${status.canClaim}, Can Battle: ${status.canBattle}`)
    } catch (error) {
      updateTest('Contract Connectivity', 'failed', error.message)
      throw error
    }
  }

  // AGC: Test 3 - OGB Token Airdrop
  const testOGBAirdrop = async () => {
    setCurrentTest('Testing OGB token airdrop...')
    updateTest('OGB Token Airdrop', 'running')
    
    try {
      if (!signer) throw new Error('No signer available')
      
      const address = await signer.getAddress()
      const initialStatus = await getPlayerOGBStatus(address)
      
      if (initialStatus.canClaim) {
        const receipt = await claimAirdrop()
        const finalStatus = await getPlayerOGBStatus(address, true)
        
        if (finalStatus.balance > initialStatus.balance) {
          updateTest('OGB Token Airdrop', 'passed', 
            `Airdrop claimed! Balance: ${initialStatus.balance} → ${finalStatus.balance} OGB`,
            `Transaction: ${receipt.hash}`)
        } else {
          throw new Error('Balance did not increase after airdrop')
        }
      } else {
        updateTest('OGB Token Airdrop', 'passed', 
          `Already claimed airdrop. Current balance: ${initialStatus.balance} OGB`)
      }
    } catch (error) {
      updateTest('OGB Token Airdrop', 'failed', error.message)
      throw error
    }
  }

  // AGC: Test 4 - Battle Cost Payment
  const testBattleCostPayment = async () => {
    setCurrentTest('Testing battle cost payment...')
    updateTest('Battle Cost Payment', 'running')
    
    try {
      if (!signer) throw new Error('No signer available')
      
      const address = await signer.getAddress()
      const canBattle = await canPlayBattle(address)
      
      if (!canBattle) {
        throw new Error('Player cannot afford battle cost (need 50 OGB)')
      }
      
      const initialStatus = await getPlayerOGBStatus(address)
      const receipt = await chargeBattleCost(address)
      const finalStatus = await getPlayerOGBStatus(address, true)
      
      const costPaid = initialStatus.balance - finalStatus.balance
      
      if (costPaid === 50) {
        updateTest('Battle Cost Payment', 'passed', 
          `Battle cost paid! Balance: ${initialStatus.balance} → ${finalStatus.balance} OGB`,
          `Transaction: ${receipt.hash}`)
      } else {
        throw new Error(`Expected 50 OGB cost, but ${costPaid} OGB was deducted`)
      }
    } catch (error) {
      updateTest('Battle Cost Payment', 'failed', error.message)
      // Don't throw - continue with other tests
    }
  }

  // AGC: Test 5 - Account Switching
  const testAccountSwitching = async () => {
    setCurrentTest('Testing account switching...')
    updateTest('Account Switching', 'running')
    
    try {
      if (!signer) throw new Error('No signer available')
      
      const currentAddress = await signer.getAddress()
      
      // AGC: Test disconnect/reconnect
      disconnectWallet()
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      if (isConnected) {
        throw new Error('Wallet should be disconnected')
      }
      
      await connectWallet()
      
      if (!isConnected) {
        throw new Error('Wallet should be reconnected')
      }
      
      updateTest('Account Switching', 'passed', 
        `Successfully disconnected and reconnected wallet`)
    } catch (error) {
      updateTest('Account Switching', 'failed', error.message)
      // Don't throw - continue with other tests
    }
  }

  // AGC: Test 6 - Error Handling
  const testErrorHandling = async () => {
    setCurrentTest('Testing error handling...')
    updateTest('Error Handling', 'running')
    
    try {
      // AGC: Test invalid address
      try {
        await getPlayerOGBStatus('0x0000000000000000000000000000000000000000')
        // Should not reach here
      } catch (error) {
        // Expected error
      }
      
      // AGC: Test unauthorized operations
      if (signer) {
        try {
          const address = await signer.getAddress()
          await processBattleResult(address, address) // Should fail - not authorized
          throw new Error('Should have failed with authorization error')
        } catch (error) {
          if (error.message.includes('authorized')) {
            // Expected error
          } else {
            throw error
          }
        }
      }
      
      updateTest('Error Handling', 'passed', 'Error handling working correctly')
    } catch (error) {
      updateTest('Error Handling', 'failed', error.message)
      // Don't throw - this is the last test
    }
  }

  // AGC: Run individual test
  const runSingleTest = async (testName: string) => {
    if (isRunning) return
    
    setIsRunning(true)
    setCurrentTest(`Running ${testName}...`)
    
    try {
      switch (testName) {
        case 'Wallet Connection':
          await testWalletConnection()
          break
        case 'Contract Connectivity':
          await testContractConnectivity()
          break
        case 'OGB Token Airdrop':
          await testOGBAirdrop()
          break
        case 'Battle Cost Payment':
          await testBattleCostPayment()
          break
        case 'Account Switching':
          await testAccountSwitching()
          break
        case 'Error Handling':
          await testErrorHandling()
          break
      }
      setCurrentTest(`✅ ${testName} completed`)
    } catch (error) {
      setCurrentTest(`❌ ${testName} failed: ${error.message}`)
    } finally {
      setIsRunning(false)
    }
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white p-6">
      <div className="max-w-4xl mx-auto">
        <div className="bg-slate-800 rounded-lg p-6 mb-6">
          <h1 className="text-3xl font-bold text-center mb-2">🧪 Origami Battles Test Suite</h1>
          <p className="text-slate-400 text-center mb-6">
            Comprehensive testing for all battle system functionality
          </p>
          
          <div className="flex gap-4 justify-center mb-6">
            {onBackToMenu && (
              <button
                onClick={onBackToMenu}
                className="bg-slate-600 hover:bg-slate-700 px-6 py-2 rounded-lg font-medium transition-colors"
              >
                ← Back to Menu
              </button>
            )}
            
            <button
              onClick={runAllTests}
              disabled={isRunning}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 px-6 py-2 rounded-lg font-medium transition-colors"
            >
              {isRunning ? '🔄 Running Tests...' : '🚀 Run All Tests'}
            </button>
            
            <button
              onClick={addTestAccount}
              className="bg-green-600 hover:bg-green-700 px-6 py-2 rounded-lg font-medium transition-colors"
            >
              📱 Add Test Account
            </button>
            
            <button
              onClick={async () => {
                console.log('🧪 Testing MetaMask connection...')
                const result = await testMetaMaskConnection()
                if (result) {
                  alert('✅ MetaMask connection test passed! Check console for details.')
                } else {
                  alert('❌ MetaMask connection test failed! Check console for details.')
                }
              }}
              className="bg-yellow-600 hover:bg-yellow-700 px-6 py-2 rounded-lg font-medium transition-colors"
            >
              🧪 Test MetaMask
            </button>
          </div>
          
          {currentTest && (
            <div className="bg-slate-700 rounded-lg p-4 mb-6">
              <p className="text-center font-medium">{currentTest}</p>
            </div>
          )}
        </div>

        <div className="grid gap-4">
          {tests.map((test, index) => (
            <div key={test.name} className="bg-slate-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">
                    {test.status === 'pending' && '⏳'}
                    {test.status === 'running' && '🔄'}
                    {test.status === 'passed' && '✅'}
                    {test.status === 'failed' && '❌'}
                  </span>
                  <h3 className="text-lg font-medium">{test.name}</h3>
                </div>
                
                <button
                  onClick={() => runSingleTest(test.name)}
                  disabled={isRunning}
                  className="bg-slate-600 hover:bg-slate-500 disabled:bg-slate-700 px-3 py-1 rounded text-sm transition-colors"
                >
                  Run
                </button>
              </div>
              
              {test.message && (
                <p className={`text-sm mb-2 ${
                  test.status === 'passed' ? 'text-green-400' : 
                  test.status === 'failed' ? 'text-red-400' : 'text-slate-400'
                }`}>
                  {test.message}
                </p>
              )}
              
              {test.details && (
                <p className="text-xs text-slate-500 font-mono">
                  {test.details}
                </p>
              )}
            </div>
          ))}
        </div>

        <div className="mt-8 bg-slate-800 rounded-lg p-6">
          <h2 className="text-xl font-bold mb-4">📋 Test Instructions</h2>
          <div className="space-y-2 text-sm text-slate-300">
            <p><strong>1. Wallet Connection:</strong> Tests MetaMask connection and network switching</p>
            <p><strong>2. Contract Connectivity:</strong> Verifies smart contract communication</p>
            <p><strong>3. OGB Token Airdrop:</strong> Tests claiming 1000 OGB tokens</p>
            <p><strong>4. Battle Cost Payment:</strong> Tests paying 50 OGB battle cost</p>
            <p><strong>5. Account Switching:</strong> Tests wallet disconnect/reconnect</p>
            <p><strong>6. Error Handling:</strong> Tests proper error responses</p>
          </div>
        </div>
      </div>
    </div>
  )
}
